<script lang="ts">
  // AuditRow — single row of the audit log table.
  //
  // PR 7 scope (R-Audit-Page from
  // openspec/changes/ui-redesign-v2/specs/dashboard-ui/spec.md):
  //
  //   - Renders timestamp (date + time), actor, action verb, target,
  //     and the result pill.
  //   - Failure rows expose an "expand" affordance that reveals the
  //     failure excerpt in-place (the table stays compact; the diff
  //     block is opt-in).
  //   - Uses semantic <tr>/<td> so screen readers announce the row
  //     context (column headers are wired in the parent PageAudit).
  //
  // Visual reference: openspec/changes/ui-redesign-v2/Untitled.fig
  // nodes 0:997-1044 (audit table rows — each row 5 cells + check).
  //
  // WCAG 2.2 AA:
  //   - aria-expanded toggles in sync with the expand state.
  //   - The expand button is reachable via Tab and activates on
  //     Enter/Space (native <button>).
  //   - The fail pill carries a data-tone attribute AND a text label
  //     so the row isn't colour-only.

  import type { AuditRow as AuditRowData } from "../lib/audit";

  export let row: AuditRowData;

  let expanded = false;

  function toggle(): void {
    expanded = !expanded;
  }

  function formatTimestamp(iso: string): string {
    // Locale-neutral short format. We rely on Date#toLocaleString
    // because happy-dom provides a stable Intl in CI; in the browser
    // the user's locale + timezone wins. The test only asserts the
    // year + HH:MM shape, never the exact string.
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  $: tsLabel = formatTimestamp(row.tsIso);
  $: hasDetails = row.details.length > 0;
</script>

<tr class="audit-row" data-testid="audit-row" data-tone={row.tone}>
  <td class="audit-row__cell audit-row__cell--ts" data-testid="audit-row-ts">
    <time datetime={row.tsIso}>{tsLabel}</time>
  </td>
  <td class="audit-row__cell audit-row__cell--actor" data-testid="audit-row-actor">
    {row.actor}
  </td>
  <td class="audit-row__cell audit-row__cell--action" data-testid="audit-row-action">
    <span class="audit-row__action-tag">{row.action}</span>
  </td>
  <td class="audit-row__cell audit-row__cell--target mono" data-testid="audit-row-target">
    {row.target}
  </td>
  <td class="audit-row__cell audit-row__cell--result">
    <span
      class="audit-row__pill audit-row__pill--{row.tone}"
      data-testid="audit-row-result"
      data-tone={row.tone}
      aria-label={`Result: ${row.result}`}
    >
      <span class="audit-row__pill-dot" aria-hidden="true"></span>
      {row.result}
    </span>
  </td>
  <td class="audit-row__cell audit-row__cell--expand">
    {#if hasDetails}
      <button
        type="button"
        class="audit-row__expand-btn"
        data-testid="audit-row-expand"
        aria-expanded={expanded ? "true" : "false"}
        aria-controls={`audit-row-details-${row.timestampMs}`}
        aria-label={expanded ? "Hide failure details" : "Show failure details"}
        on:click={toggle}
      >
        {expanded ? "Hide" : "Details"}
      </button>
    {/if}
  </td>
</tr>
{#if expanded && hasDetails}
  <tr
    class="audit-row__details"
    id={`audit-row-details-${row.timestampMs}`}
    data-testid="audit-row-details"
  >
    <td colspan="6">
      <pre class="audit-row__details-body mono">{row.details}</pre>
    </td>
  </tr>
{/if}

<style>
  .audit-row {
    background: var(--surface);
    border-bottom: 1px solid var(--border);
  }
  .audit-row[data-tone="fail"] {
    background: rgba(239, 68, 68, 0.04);
  }
  .audit-row__cell {
    padding: var(--space-3) var(--space-4);
    color: var(--text-strong);
    font: var(--type-body-sm);
    vertical-align: middle;
  }
  .audit-row__cell--ts {
    color: var(--text-mid);
    font: var(--type-mono-md);
    white-space: nowrap;
  }
  .audit-row__action-tag {
    display: inline-flex;
    height: 22px;
    align-items: center;
    padding: 0 var(--space-2);
    border-radius: 999px;
    background: var(--surface-2);
    border: 1px solid var(--border);
    color: var(--text-mid);
    font: var(--type-label);
    text-transform: lowercase;
    letter-spacing: 0.02em;
  }
  .audit-row__cell--target {
    color: var(--text-strong);
    word-break: break-all;
  }
  .audit-row__pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 22px;
    padding: 0 var(--space-2);
    border-radius: 999px;
    font: var(--type-label);
    font-weight: 500;
    border: 1px solid var(--border);
  }
  .audit-row__pill-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
  .audit-row__pill--ok {
    color: var(--ok-green);
    background: var(--ok-bg);
    border-color: rgba(34, 197, 94, 0.40);
  }
  .audit-row__pill--fail {
    color: var(--err-red);
    background: var(--err-bg);
    border-color: rgba(239, 68, 68, 0.40);
  }
  .audit-row__expand-btn {
    background: transparent;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-mid);
    font: var(--type-label);
    padding: 2px var(--space-2);
    cursor: pointer;
  }
  .audit-row__expand-btn:hover {
    color: var(--text-strong);
    background: var(--surface-2);
  }
  .audit-row__expand-btn[aria-expanded="true"] {
    color: var(--accent-cyan);
    border-color: rgba(79, 70, 229, 0.40);
  }
  .audit-row__details {
    background: var(--surface);
    border-bottom: 1px solid var(--border);
  }
  .audit-row__details-body {
    margin: 0;
    padding: var(--space-3) var(--space-4);
    color: var(--err-red);
    font: var(--type-mono-md);
    background: var(--err-bg);
    border-left: 3px solid var(--err-red);
    white-space: pre-wrap;
    word-break: break-word;
  }
</style>
